import { BlockType, CollectionViewType } from '@next-space/fe-api-idl';
import type { FC, ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BitableContext } from 'src/bitable/context';
import { FormContainer } from 'src/bitable/form-view/form-container';
import { FormErrorPageView } from 'src/bitable/form-view/form-error-page-view';
import { ShareFormView } from 'src/bitable/form-view/share-form-view';
import { LoadingContainer } from 'src/common/components/loading-container';
import { message } from 'src/common/components/message';
import { NextModalProvider } from 'src/common/components/next-modal';
import { UNTITLED_FORM } from 'src/common/const';
import { request } from 'src/common/request';
import { useFetchActivities } from 'src/hooks/activities/use-fetch-activities';
import { useFetchRootPages } from 'src/hooks/page';
import { useGetQuery } from 'src/hooks/page/use-get-query';
import { blocksActions } from 'src/redux/reducers/blocks';
import { collectionViewsActions } from 'src/redux/reducers/collection-views';
import { cache, dispatch } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { updatePublicSpaces } from 'src/services/public-space';
import { $currentUserCache } from 'src/services/user/current-user';
import { SearchParams, ViewPath } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { setBrowserTitle } from 'src/utils/emoji-favicon';
import { useGetPageId } from 'src/utils/getPageId';
import { splicePathSearchParams } from 'src/utils/history-utils';
import { HeaderCover } from './main/page-doc/header-cover';

export const ShareFormPage: FC = () => {
  const fetchActivities = useFetchActivities();
  const shareId = useGetPageId();
  const history = useHistory();
  const fetchRootPages = useFetchRootPages();
  const [errorPage, setErrorPage] = useState<ReactElement | null>(null);
  const [context, setContext] = useState<{
    viewId: string;
    collectionId: string;
    viewParentId: string;
  } | null>(null);
  const getQuery = useGetQuery();
  const inviteCode = getQuery(SearchParams.inviteCode);

  useEffect(() => {
    if (shareId) {
      void (async () => {
        const res = await request.editor.formViewGetBlock.raw(shareId);
        if (res.code === 200) {
          if (res.data.view && res.data.view.type === CollectionViewType.FORM) {
            if ($currentUserCache.uuid) {
              void fetchRootPages(res.data.view.spaceId, {
                notJumpLocation: true,
              });
            }
            dispatch(
              blocksActions.update({ blocks: res.data.blocks as Record<string, NextBlock> })
            );
            const spacePublic = await request.infra.getSpacesPublicData({
              spaceIds: [res.data.view.spaceId],
            });
            updatePublicSpaces(spacePublic.spaces);
            dispatch(collectionViewsActions.update({ [res.data.view.uuid]: res.data.view }));
            // 判断parent是不是引用表
            const parentBlock = cache.blocks[res.data.view.parentId];
            if (parentBlock?.type === BlockType.REFERENCE_COLLECTION) {
              // 找到引用表的ref uuid
              if (!parentBlock.data.ref?.uuid) {
                setErrorPage(<FormErrorPageView icon="😑" description="链接失效" />);
                return;
              }
              // 通过ref uuid找到引用表
              const collection = cache.blocks[parentBlock.data.ref.uuid];
              if (!collection) {
                setErrorPage(<FormErrorPageView icon="😑" description="链接失效" />);
                return;
              }
              setContext({
                viewId: res.data.view.uuid,
                collectionId: collection.uuid,
                viewParentId: res.data.view.parentId,
              });
              bizTracker.event('page_enter', {
                is_success: true,
                page_type: collection.type,
                page_id: collection.uuid,
                is_shared: true,
              });
            } else {
              // 判断已经登录的情况下，才更新活动列表
              if ($currentUserCache.uuid) {
                void fetchActivities();
              }
              setContext({
                viewId: res.data.view.uuid,
                collectionId: res.data.view.parentId,
                viewParentId: res.data.view.parentId,
              });
              bizTracker.event('page_enter', {
                is_success: true,
                page_type: parentBlock?.type,
                page_id: res.data.view.parentId,
                is_shared: true,
              });
            }
            void setBrowserTitle({
              title: res.data.view.title || UNTITLED_FORM,
              icon: parentBlock?.data.icon,
            });
          } else {
            setErrorPage(<FormErrorPageView icon="😑" description="链接失效" />);
          }
        } else {
          switch (res.code) {
            case 1102:
              message.error('请先登录');
              history.push(
                splicePathSearchParams(ViewPath.login, [
                  {
                    key: SearchParams.callbackPath,
                    value: location.pathname,
                  },
                  {
                    key: SearchParams.inviteCode,
                    value: inviteCode,
                  },
                ])
              );
              return;
            case 1403:
              setErrorPage(<FormErrorPageView icon="✋" description="无权限访问" />);
              break;
            case 1600:
              setErrorPage(
                <FormErrorPageView
                  icon="🚫"
                  description={
                    <>
                      <div className="text-center mb-1">无法访问该页面</div>
                      <div className="text-grey3 text-t2">
                        当前页面违反平台分享协作条例，已禁止访问
                      </div>
                    </>
                  }
                />
              );
              break;
            case 5003:
              setErrorPage(<FormErrorPageView icon="😊" description="你已提交收集表" />);
              break;
            default:
              setErrorPage(<FormErrorPageView icon="😑" description={res.msg} />);
          }
        }
      })();
    } else {
      message.error('链接有误');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId]);

  if (errorPage) {
    return (
      <FormContainer
        className="max-h-[500px]"
        header={
          <HeaderCover uuid={`uuid`} ossName={'cover/flowus2'} hiddenEditorButton height={320} />
        }
      >
        {errorPage}
      </FormContainer>
    );
  }

  if (!context) return <LoadingContainer />;

  return (
    <BitableContext.Provider
      value={{
        ...context,
        readonly: true,
        managerReadonly: true,
        changeView: () => {},
        isColumnListChildren: false,
        isFoldListChildren: false,
        viewType: CollectionViewType.FORM,
      }}
    >
      <NextModalProvider>
        <div className="form-scroll-container overflow-y-auto h-full min-h-screen">
          <ShareFormView shareId={shareId} />
        </div>
      </NextModalProvider>
    </BitableContext.Provider>
  );
};
